import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';

interface AnalysisData {
  id: string;
  measurement_date: string;
  name: string;
  distance: number;
  data: {
    speed?: number;
    pitch?: number;
    stride?: number;
    contactTime?: number;
    flightTime?: number;
    predicted100m?: number;
  };
  comment?: string;
  created_at: string;
  updated_at: string;
}

interface HybridAnalysisState {
  localResults: AnalysisData[];
  cloudResults: AnalysisData[];
  isLoading: boolean;
  error: string | null;
  syncStatus: 'idle' | 'syncing' | 'error';
  lastSyncTime: string | null;
  
  addLocalResult: (data: Omit<AnalysisData, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  deleteLocalResult: (id: string) => Promise<void>;
  fetchCloudResults: () => Promise<void>;
  syncToCloud: () => Promise<void>;
  retrySync: () => Promise<void>;
}

export const useHybridAnalysisStore = create<HybridAnalysisState>()(
  persist(
    (set, get) => ({
      localResults: [],
      cloudResults: [],
      isLoading: false,
      error: null,
      syncStatus: 'idle',
      lastSyncTime: null,

      addLocalResult: async (data) => {
        try {
          set({ isLoading: true, error: null });

          // セッション取得を最適化
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.user?.id) {
            throw new Error('認証されていません');
          }

          // 新しい結果オブジェクトを作成
          const newResult: AnalysisData = {
            id: crypto.randomUUID(),
            measurement_date: data.measurement_date,
            name: data.name,
            distance: data.distance,
            data: data.data || {},
            comment: data.comment,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          // ローカルステートに追加
          set(state => ({
            localResults: [newResult, ...state.localResults],
            error: null
          }));

          // クラウドに同期
          const { error: insertError } = await supabase
            .from('analysis_data')
            .insert({
              id: newResult.id,
              user_id: session.user.id,
              measurement_date: newResult.measurement_date,
              name: newResult.name,
              distance: newResult.distance,
              data: newResult.data,
              comment: newResult.comment
            });

          if (insertError) throw insertError;

          // 同期成功時の状態更新
          set(state => ({
            cloudResults: [newResult, ...state.cloudResults],
            lastSyncTime: new Date().toISOString(),
            syncStatus: 'idle'
          }));

        } catch (err) {
          console.error('Error adding result:', err);
          set({ 
            error: err instanceof Error ? err.message : 'データの保存に失敗しました',
            syncStatus: 'error'
          });
        } finally {
          set({ isLoading: false });
        }
      },

      deleteLocalResult: async (id) => {
        try {
          set({ isLoading: true, error: null });

          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.user?.id) {
            throw new Error('認証されていません');
          }

          // クラウドから削除
          const { error: deleteError } = await supabase
            .from('analysis_data')
            .delete()
            .match({ id, user_id: session.user.id });

          if (deleteError) throw deleteError;

          // ローカルステートを更新
          set(state => ({
            localResults: state.localResults.filter(result => result.id !== id),
            cloudResults: state.cloudResults.filter(result => result.id !== id),
            lastSyncTime: new Date().toISOString(),
            syncStatus: 'idle',
            error: null
          }));

        } catch (err) {
          console.error('Error deleting result:', err);
          set({ 
            error: err instanceof Error ? err.message : 'データの削除に失敗しました',
            syncStatus: 'error'
          });
        } finally {
          set({ isLoading: false });
        }
      },

      fetchCloudResults: async () => {
        try {
          set({ isLoading: true, error: null });

          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.user?.id) {
            throw new Error('認証されていません');
          }

          const { data, error } = await supabase
            .from('analysis_data')
            .select('*')
            .eq('user_id', session.user.id)
            .order('measurement_date', { ascending: false });

          if (error) throw error;

          const results = data?.map(item => ({
            id: item.id,
            measurement_date: item.measurement_date,
            name: item.name,
            distance: item.distance,
            data: item.data || {},
            comment: item.comment,
            created_at: item.created_at,
            updated_at: item.updated_at
          })) || [];

          set({ 
            localResults: results,
            cloudResults: results,
            lastSyncTime: new Date().toISOString(),
            syncStatus: 'idle',
            error: null
          });

        } catch (err) {
          console.error('Error fetching results:', err);
          set({ 
            error: err instanceof Error ? err.message : 'データの取得に失敗しました',
            syncStatus: 'error'
          });
        } finally {
          set({ isLoading: false });
        }
      },

      syncToCloud: async () => {
        const state = get();
        if (state.syncStatus === 'syncing') return;

        try {
          set({ syncStatus: 'syncing', error: null });

          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.user?.id) {
            await supabase.auth.refreshSession();
            throw new Error('認証されていません');
          }

          const localOnlyData = state.localResults.filter(
            local => !state.cloudResults.some(cloud => cloud.id === local.id)
          );

          if (localOnlyData.length === 0) {
            set({ syncStatus: 'idle' });
            return;
          }

          // 一件ずつ同期して確実性を高める
          for (const result of localOnlyData) {
            const { error: syncError } = await supabase
              .from('analysis_data')
              .insert({
                id: result.id,
                user_id: session.user.id,
                measurement_date: result.measurement_date,
                name: result.name,
                distance: result.distance,
                data: result.data,
                comment: result.comment
              });

            if (syncError) throw syncError;
          }

          // 同期成功時の状態更新
          set(state => ({
            cloudResults: [...localOnlyData, ...state.cloudResults],
            lastSyncTime: new Date().toISOString(),
            syncStatus: 'idle',
            error: null
          }));

        } catch (err) {
          console.error('Sync error:', err);
          set({ 
            error: err instanceof Error ? err.message : '同期に失敗しました',
            syncStatus: 'error'
          });
        }
      },

      retrySync: async () => {
        set({ error: null, syncStatus: 'idle' });
        await get().syncToCloud();
      }
    }),
    {
      name: 'sprint-analysis-storage',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          try {
            return JSON.parse(str);
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
      partialize: (state) => ({
        localResults: state.localResults,
        cloudResults: state.cloudResults,
        lastSyncTime: state.lastSyncTime
      })
    }
  )
);